import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  DispatchDetailStop,
  DispatchDisposition,
} from '@/services/api/dispatches';

import {
  ItemConditionBadge,
  ItemStatusBadge,
} from '../components/DispatchBadges';

interface CancelStopItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The stop being removed. Null = modal closed / not opened. */
  stop: DispatchDetailStop | null;
  onSubmit: (
    stopId: string,
    disposition: DispatchDisposition,
    reason: string
  ) => Promise<void>;
}

const CHOICES: Array<{
  key: DispatchDisposition;
  label: string;
  hint: string;
}> = [
  {
    key: 'CANCEL',
    label: 'Batalkan stop (Cancel)',
    hint: 'Semua item di stop ini dibatalkan (status Cancelled) dan stop dikeluarkan dari dispatch. Terminal — tidak bisa dikembalikan.',
  },
  {
    key: 'LOST',
    label: 'Tandai stop hilang (Lost)',
    hint: 'Semua item di stop ini ditandai hilang (status Lost) dan stop dikeluarkan dari dispatch. Pakai untuk barang yang tidak akan pernah datang.',
  },
  {
    key: 'DEFER',
    label: 'Tahan stop (Defer)',
    hint: 'Stop ini (beserta semua itemnya) dipindah ke daftar Ditahan — keluar dari rute rider dan tidak ikut terkirim saat commit. Bisa dikembalikan dengan meng-assign-nya ke rider lagi.',
  },
];

/**
 * Dispose of all items at a stop on a PREVIEW dispatch — cancel/lost (terminal,
 * stop dropped) or defer (reversible). Re-render from the returned detail.
 */
const CancelStopItemsModal: React.FC<CancelStopItemsModalProps> = ({
  isOpen,
  onClose,
  stop,
  onSubmit,
}) => {
  const [choice, setChoice] = useState<DispatchDisposition>('CANCEL');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChoice('CANCEL');
      setReason('');
    }
  }, [isOpen]);

  const terminal = choice !== 'DEFER';

  const handleSubmit = async () => {
    if (!stop) return;
    setSubmitting(true);
    try {
      await onSubmit(stop.id, choice, reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel =
    choice === 'CANCEL'
      ? 'Batalkan stop'
      : choice === 'LOST'
      ? 'Tandai hilang'
      : 'Tahan stop';

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-alert-line" />}
      iconTone={terminal ? 'danger' : 'warning'}
      title="Remove stop"
      subtitle={stop ? stop.receiverName || stop.destinationAddress : undefined}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button
            tone={terminal ? 'destructive' : 'primary'}
            onClick={handleSubmit}
            disabled={submitting || !stop}
          >
            {submitting ? 'Memproses…' : actionLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {stop && (
          <div className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
            <p className="text-[13px] font-semibold text-text-strong-950">
              {stop.itemCount} item di stop ini
            </p>
            <ul className="mt-1.5 space-y-1">
              {stop.items.map((it) => (
                <li
                  key={it.id}
                  className="flex items-center gap-2 text-[11px] text-text-sub-600"
                >
                  <span className="font-mono font-semibold text-text-strong-950 tabular-nums">
                    {it.waybill}
                  </span>
                  <span className="truncate">{it.itemName}</span>
                  <ItemStatusBadge
                    status={it.itemStatus}
                    skipsHubScan={it.skipsHubScan}
                  />
                  <ItemConditionBadge condition={it.condition} />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Tindakan untuk stop ini
          </p>
          <div className="rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
            {CHOICES.map((c) => {
              const checked = choice === c.key;
              return (
                <label
                  key={c.key}
                  className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    checked ? 'bg-bg-weak-50 border-l-2 border-l-accent' : 'border-l-2 border-l-transparent hover:bg-bg-weak-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="stop-disposition"
                    checked={checked}
                    onChange={() => setChoice(c.key)}
                    disabled={submitting}
                    className="mt-0.5 h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-text-strong-950 tracking-tight">
                      {c.label}
                    </span>
                    <span className="block text-[12px] text-text-sub-600 leading-relaxed">
                      {c.hint}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="stop-disposition-reason"
            className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Alasan (opsional)
          </label>
          <textarea
            id="stop-disposition-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Contoh: barang tidak ada saat cek sore…"
            disabled={submitting}
            className="w-full rounded-lg border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:bg-bg-soft-200 disabled:cursor-not-allowed disabled:text-text-sub-600"
          />
        </div>
      </div>
    </FormModal>
  );
};

export default CancelStopItemsModal;
